import React, { useEffect, useState } from 'react';
import { API_BASE } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import './TenantList.css';

const TenantList = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/admin/tenants?page=${page}&size=${size}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to fetch tenants');

      const data = await res.json();
      setTenants((data.content || []).map(t => ({ ...t, id: Number(t.id) })));
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Tenant fetch error:', err);
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchTenants();
  }, [token, page, size]);

  const filtered = tenants.filter((t) => {
    const email = t.email?.toLowerCase() || '';
    const plan = t.plan?.toLowerCase() || '';
    return email.includes(query.toLowerCase()) || plan.includes(query.toLowerCase());
  });

  const handlePrevious = () => {
    if (page > 0) setPage((prev) => prev - 1);
  };

  const handleNext = () => {
    if (page < totalPages - 1) setPage((prev) => prev + 1);
  };

  const handleCreateTenant = () => {
    navigate('/admin/tenants/new');
  };

  return (
    <div className="tenant-list">
      <div className="tenant-list-header">
      <h2>Tenants</h2>
          <button className="create-tenant-btn" onClick={handleCreateTenant}>
          Create New Tenant
        </button>
      </div>

      <input
        type="text"
        placeholder="Search by email or plan..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div style={{ marginBottom: '1rem' }}>
        <label>Page size: </label>
        <select
          value={size}
          onChange={(e) => {
            setSize(parseInt(e.target.value));
            setPage(0);
          }}
        >
          {[5, 10, 20, 50].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {loading && <p>Loading tenants...</p>}
      {error && <p className="error-message">{error}</p>}

      {!loading && !error && (
        <>
          {filtered.length === 0 ? (
            <p className="no-results">No tenants found.</p>
          ) : (
            <table className="tenant-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Token Limit</th>
                  <th>Tokens Used</th>
                  <th>Detokenizations</th>
                  <th>Role</th>
                  <th>Has API Key</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id}>
                    <td>{t.id}</td>
                    <td>{t.email ?? '—'}</td>
                    <td>{t.plan ?? '—'}</td>
                    <td>{t.tokenLimitPerMonth ?? 0}</td>
                    <td>{t.tokensUsedThisMonth ?? 0}</td>
                    <td>{t.detokenizationsUsedThisMonth ?? 0}</td>
                    <td>{t.role ?? '—'}</td>
                    <td>{t.hasApiKey ? '✅' : '❌'}</td>
                    <td>
                      <Link to={`/admin/tenants/${t.id}`}>View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="pagination">
            <button onClick={handlePrevious} disabled={page === 0}>
              ◀ Previous
            </button>
            <span>
              Page {page + 1} of {totalPages}
            </span>
            <button onClick={handleNext} disabled={page >= totalPages - 1}>
              Next ▶
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TenantList;
